import { createContext, useContext, useReducer, ReactNode } from 'react';
import produce from 'immer';

import { unimplemented } from '@utils/commonUtils';

type AccountSettingsContextKeys =
  | 'account.settings.users.query'
  | 'account.settings.users.active_role'
  | 'account.settings.users.active_status'
  | 'account.settings.users.selected_row'
  | 'account.settings.users.selected_users'
  | 'account.settings.users.current_organization'
  | 'account.settings.users.invite_form'
  | 'account.settings.users.delete_modal';

const ACCOUNT_SETTINGS_CONTEXT_KEYS = {
  QUERY: 'account.settings.users.query' as AccountSettingsContextKeys,
  ACTIVE_ROLE: 'account.settings.users.active_role' as AccountSettingsContextKeys,
  ACTIVE_STATUS: 'account.settings.users.active_status' as AccountSettingsContextKeys,
  SELECTED_ROW: 'account.settings.users.selected_row' as AccountSettingsContextKeys,
  SELECTED_USERS: 'account.settings.users.selected_users' as AccountSettingsContextKeys,
  CURRENT_ORGANIZATION: 'account.settings.users.current_organization' as AccountSettingsContextKeys,
  INVITE_FORM: 'account.settings.users.invite_form' as AccountSettingsContextKeys,
  REMOVE_MODAL: 'account.settings.users.delete_modal' as AccountSettingsContextKeys,
};

export interface IAccountSettingsContext {
  query: string;
  selectedRow: string | null;
  activeRole: string;
  activeStatus: string;
  selectedUsers: string[];
  currentOrganization: string | undefined;
  isInviteUserFormVisible: boolean;
  isRemoveUserModalActive: boolean;
  setQuery: (val: string) => void;
  setActiveRole: (val: string) => void;
  setActiveStatus: (val: string) => void;
  setSelectedUsers: (val: string[]) => void;
  setIsInviteUserFormVisible: (val: boolean) => void;
  setIsRemoveUserModalActive: (val: boolean) => void;
  setCurrentOrganization: (val: string) => void;
  setSelectedRow: (val: string | null) => void;
}

const initialState: IAccountSettingsContext = {
  query: '',
  activeRole: 'all',
  activeStatus: 'all',
  selectedUsers: [],
  selectedRow: null,
  currentOrganization: undefined,
  isInviteUserFormVisible: false,
  isRemoveUserModalActive: false,
  setQuery: unimplemented,
  setActiveRole: unimplemented,
  setActiveStatus: unimplemented,
  setIsInviteUserFormVisible: unimplemented,
  setSelectedUsers: unimplemented,
  setCurrentOrganization: unimplemented,
  setIsRemoveUserModalActive: unimplemented,
  setSelectedRow: unimplemented,
};

type Action = {
  type: AccountSettingsContextKeys;
  payload: boolean | null | string | string[];
};

const reducer = produce((draft: IAccountSettingsContext, action: Action) => {
  const { type, payload } = action;

  switch (type) {
    case ACCOUNT_SETTINGS_CONTEXT_KEYS.ACTIVE_ROLE:
      draft.activeRole = payload as string;

      break;
    case ACCOUNT_SETTINGS_CONTEXT_KEYS.ACTIVE_STATUS:
      draft.activeStatus = payload as string;

      break;
    case ACCOUNT_SETTINGS_CONTEXT_KEYS.INVITE_FORM:
      draft.isInviteUserFormVisible = payload as boolean;

      break;

    case ACCOUNT_SETTINGS_CONTEXT_KEYS.SELECTED_USERS:
      draft.selectedUsers = payload as string[];

      break;

    case ACCOUNT_SETTINGS_CONTEXT_KEYS.QUERY:
      draft.query = payload as string;

      break;

    case ACCOUNT_SETTINGS_CONTEXT_KEYS.REMOVE_MODAL:
      draft.isRemoveUserModalActive = payload as boolean;

      break;

    case ACCOUNT_SETTINGS_CONTEXT_KEYS.CURRENT_ORGANIZATION:
      draft.currentOrganization = payload as string;

      break;

    case ACCOUNT_SETTINGS_CONTEXT_KEYS.SELECTED_ROW:
      draft.selectedRow = payload as string | null;

      break;
  }
}, initialState);

export const AccountSettingsContext = createContext<IAccountSettingsContext>(initialState);

export const AccountSettingsProvider = ({ children }: { children: ReactNode }) => {
  const [state, dispatch] = useReducer(reducer, initialState);

  const setQuery = (payload: string) => {
    dispatch({ type: ACCOUNT_SETTINGS_CONTEXT_KEYS.QUERY, payload });
  };

  const setActiveRole = (payload: string) => {
    dispatch({ type: ACCOUNT_SETTINGS_CONTEXT_KEYS.ACTIVE_ROLE, payload });
  };

  const setActiveStatus = (payload: string) => {
    dispatch({ type: ACCOUNT_SETTINGS_CONTEXT_KEYS.ACTIVE_STATUS, payload });
  };

  const setIsInviteUserFormVisible = (payload: boolean) => {
    dispatch({ type: ACCOUNT_SETTINGS_CONTEXT_KEYS.INVITE_FORM, payload });
  };

  const setSelectedUsers = (payload: string[]) => {
    dispatch({ type: ACCOUNT_SETTINGS_CONTEXT_KEYS.SELECTED_USERS, payload });
  };

  const setIsRemoveUserModalActive = (payload: boolean) => {
    dispatch({ type: ACCOUNT_SETTINGS_CONTEXT_KEYS.REMOVE_MODAL, payload });
  };

  const setCurrentOrganization = (payload: string) => {
    dispatch({ type: ACCOUNT_SETTINGS_CONTEXT_KEYS.CURRENT_ORGANIZATION, payload });
  };

  const setSelectedRow = (payload: string | null) => {
    dispatch({ type: ACCOUNT_SETTINGS_CONTEXT_KEYS.SELECTED_ROW, payload });
  };

  const values = {
    ...state,
    setQuery,
    setActiveRole,
    setActiveStatus,
    setSelectedUsers,
    setIsRemoveUserModalActive,
    setIsInviteUserFormVisible,
    setCurrentOrganization,
    setSelectedRow,
  };
  return (
    <AccountSettingsContext.Provider value={values}>{children}</AccountSettingsContext.Provider>
  );
};

export const useAccountSettingContext = () => {
  return useContext(AccountSettingsContext);
};
